import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Collapse, IconButton, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { minimalInputTheme } from '@/app/theme';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { CLIENT_NOTE_HELP_TEXT, DEAL_OVERVIEW_HELP_TEXT } from '@/utils/helperText';
import { useTabContext } from '@/components/layouts/TabHeader/TabContext';

export const OverviewField = () => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState(false);

	const textRef = useRef(null);
	const [collapsedSize, setCollapsedSize] = useState('0px');
	const [isClipped, setIsClipped] = useState(false);

	const { clientRow, dealRow, isClientView: isClient } = useObjectContext();

	const isDeal = Boolean(dealRow) || !isClient;

	const { isEditing, fields, setFields } = useTabContext();

	const inputProps = {
		...(theme?.typography?.avatar_subtitle ?? {}),
		fontSize: '12px',
	};

	const currentValue = (isDeal ? dealRow?.dealOverview : clientRow?.clientNote) ?? '';

	useLayoutEffect(() => {
		const el = textRef.current;
		if (el) {
			const styles = window.getComputedStyle(el);
			const lineHeight = parseFloat(styles.lineHeight);
			const size = lineHeight * 3;

			const prevClamp = el.style.webkitLineClamp;
			el.style.webkitLineClamp = 'unset';

			const fullHeight = el.scrollHeight;
			el.style.webkitLineClamp = prevClamp;

			setCollapsedSize(`${Math.min(size, fullHeight)}px`);
			setIsClipped(fullHeight > size);
		}
	}, [currentValue]);

	return (
		<>
			{isEditing ? (
				<TextField
					fullWidth
					multiline
					rows={3}
					size={'small'}
					placeholder={isDeal ? DEAL_OVERVIEW_HELP_TEXT : CLIENT_NOTE_HELP_TEXT}
					value={fields.overview}
					onChange={(event) => setFields('overview', event.target.value)}
					variant='standard'
					sx={{
						...minimalInputTheme,
						fontWeight: 500,
						fontSize: '14px',
						'& .MuiTextField-root': {
							...inputProps,
						},
						'& .MuiInputBase-input': {
							...inputProps,
						},
					}}
					slotProps={{
						input: {
							sx: {
								...inputProps,
								backgroundColor: 'transparent',
							},
						},
					}}
				/>
			) : (
				<Stack>
					<Collapse in={expanded} collapsedSize={collapsedSize}>
						<Typography
							ref={textRef}
							component='div'
							variant='avatar_subtitle'
							sx={{
								fontSize: '12px',
								...(!currentValue && { color: theme.palette.text.disabled }),
								overflow: 'hidden',
								whiteSpace: 'normal',
							}}
						>
							{!currentValue
								? `Add a ${isDeal ? 'deal overview' : 'client note'} using the edit icon`
								: currentValue}
						</Typography>
					</Collapse>
					{isClipped && (
						<Box display='flex' justifyContent='flex-end' width='100%'>
							<IconButton size='small' onClick={() => setExpanded((prev) => !prev)}>
								{expanded ? (
									<ExpandLessIcon fontSize='1.25rem' />
								) : (
									<MoreHorizIcon fontSize='1.25rem' />
								)}
							</IconButton>
						</Box>
					)}
				</Stack>
			)}
		</>
	);
};
